import React, { useCallback, useRef } from "react";
import {
  Text,
  Card,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Flex,
  HelpIndicator,
  useCopy,
} from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { Colors } from "@jsq/ds/styles";
import { PAYMENT_REFERENCE_TOOLTIP_TEXT } from "../../../constants";
import { EntityBankAccountDisplay } from "../../../EntitySelector/EntityBankAccountDisplay";
import Money from "../../../Money";
import { PayeeDisplay } from "../PayeeDisplay";
import { cardTitle, tableRow, tableCell, sectionLabelCell } from "./styles_vanilla.css";
import { PaymentDetailsCardProps } from "./types";

export const PaymentDetailsCard: React.FC<PaymentDetailsCardProps> = ({
  bankAccount,
  entity,
  createdDate,
  paymentDate,
  paymentAmount,
  paymentDescription,
  payeeId,
  referenceNumber,
  bankAccountUrl,
}) => {
  const { copy, attemptedCopy } = useCopy({ useTimeout: true });
  const tooltipIdRef = useRef(`tooltip-${Math.random().toString(36).slice(2, 11)}`);
  const tooltipId = tooltipIdRef.current;

  const handleCopy = useCallback(() => {
    if (referenceNumber) {
      void copy(referenceNumber);
    }
  }, [copy, referenceNumber]);

  return (
    <Card padding="lg">
      <Text className={cardTitle} variant="secondary-md">
        PAYMENT DETAILS
      </Text>
      <Table condensed>
        <TableBody>
          {bankAccount && (
            <>
              <TableRow className={tableRow}>
                <TableCell className={sectionLabelCell} colSpan={2}>
                  <Text variant="secondary-md">Send from:</Text>
                </TableCell>
              </TableRow>
              <TableRow className={tableRow}>
                <TableCell colSpan={2}>
                  <EntityBankAccountDisplay
                    selectedEntity={{
                      id: entity.id,
                      name: entity.name,
                      bankAccount,
                    }}
                    bankAccountUrl={bankAccountUrl}
                  />
                </TableCell>
              </TableRow>
            </>
          )}
          <TableRow className={tableRow}>
            <TableCell className={tableCell}>
              <Text variant="secondary-md">Created date</Text>
            </TableCell>
            <TableCell>
              <Text variant="regular-md">{createdDate}</Text>
            </TableCell>
          </TableRow>
          <TableRow className={tableRow}>
            <TableCell className={tableCell}>
              <Text variant="secondary-md">Payment date</Text>
            </TableCell>
            <TableCell>
              <Text variant="regular-md">{paymentDate}</Text>
            </TableCell>
          </TableRow>
          <TableRow className={tableRow}>
            <TableCell className={tableCell}>
              <Text variant="secondary-md">Payment amount</Text>
            </TableCell>
            <TableCell>
              <Money money={paymentAmount} rounded={false} />
            </TableCell>
          </TableRow>
          {referenceNumber && (
            <TableRow className={tableRow}>
              <TableCell className={tableCell}>
                <Flex alignItems="center" gap={4}>
                  <Text variant="secondary-md">Payment reference number</Text>
                  <HelpIndicator
                    id={tooltipId}
                    placement="top"
                    content={PAYMENT_REFERENCE_TOOLTIP_TEXT}
                  >
                    <Icon type="info-circle" variant="outline" color={Colors.Neutral600} />
                  </HelpIndicator>
                </Flex>
              </TableCell>
              <TableCell>
                <Flex alignItems="center" gap={8}>
                  <Text variant="regular-md">{referenceNumber}</Text>
                  <Button
                    icon={attemptedCopy ? "check" : "copy"}
                    variant="muted"
                    onClick={handleCopy}
                    aria-label="Copy payment reference"
                  />
                </Flex>
              </TableCell>
            </TableRow>
          )}
          {paymentDescription && (
            <TableRow className={tableRow}>
              <TableCell className={tableCell}>
                <Text variant="secondary-md">Payment description for recipients</Text>
              </TableCell>
              <TableCell>
                <Text variant="regular-md">{paymentDescription}</Text>
              </TableCell>
            </TableRow>
          )}
          <TableRow className={tableRow}>
            <TableCell className={sectionLabelCell} colSpan={2}>
              <Text variant="secondary-md">Send to:</Text>
            </TableCell>
          </TableRow>
          <TableRow className={tableRow}>
            <TableCell colSpan={2}>
              <PayeeDisplay payeeId={payeeId} />
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </Card>
  );
};
